import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import "../../../assets/css/Calendar.css";
import "../../../assets/css/Text.css";
import {
  DateRange,
  DayPicker,
  SelectRangeEventHandler,
} from "react-day-picker";
import InputLabel from "./inputLabel";
import NewButton from "../Buttons/newButton";
import Close from "../Icons/Close";
import CalendarMonth from "../Icons/CalendarMonth";
import { TrashCan } from "../Icons/TrashCan";
import { formatDateToString } from "../../../utils/functions";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import "../../../assets/css/InputText.css";

interface InputProps {
  title: string;
  errorMsj?: string;
  startDate?: Date;
  endDate?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  value?: string;
  defaultValue?: string;
  type: "single" | "range";
  selectDate?: SelectRangeEventHandler | undefined;
  onChange: (e: any) => void;
  onClear?: () => void;
  tooltip?: string;
  minDate?: Date;
  maxDate?: Date;
}

const CalendarInput: React.FC<InputProps> = (props) => {
  const [focus, setFocus] = useState<boolean>(false);
  const [selected, setSelected] = useState<Date>();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>();
  const [appliedDate, setAppliedDate] = useState<Date>();
  const [appliedDateRange, setAppliedDateRange] = useState<DateRange>();
  const [value, setValue] = useState<string>(props.value || "");
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [showCalendarAbove, setShowCalendarAbove] = useState<boolean>(false);
  const { isStaffMode } = useStaffMode();
  const colorClass = isStaffMode ? "staff-mode-calendar" : "";
  const calendarRef = useRef<HTMLDivElement>(null);
  const calendarDropdownRef = useRef<HTMLDivElement>(null);
  const prevPropsValueRef = useRef<string | undefined>(props.value);
  const cleanupRef = useRef<{
    timeoutId1?: NodeJS.Timeout;
    timeoutId2?: NodeJS.Timeout;
    resizeObserver?: ResizeObserver | null;
    checkInterval?: NodeJS.Timeout;
  }>({});

  const { normalizedMinDate, normalizedMaxDate } = useMemo(() => {
    let min: Date | undefined = undefined;
    let max: Date | undefined = undefined;

    if (props.minDate) {
      min = new Date(props.minDate);
      min.setHours(0, 0, 0, 0);
    }

    if (props.maxDate) {
      max = new Date(props.maxDate);
      max.setHours(0, 0, 0, 0);
    }

    if (min && max && min > max) {
      [min, max] = [max, min];
    }

    return { normalizedMinDate: min, normalizedMaxDate: max };
  }, [props.minDate, props.maxDate]);

  const parseDateString = (dateString: string): Date | undefined => {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? undefined : date;
  };

  const parseUserInputDate = (input: string): Date | undefined => {
    if (!input || input.trim() === "") {
      return undefined;
    }
    
    const mmddyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(input);
    if (mmddyyyy) {
      const month = parseInt(mmddyyyy[1], 10) - 1;
      const day = parseInt(mmddyyyy[2], 10);
      const year = parseInt(mmddyyyy[3], 10);
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime()) && date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
        return date;
      }
    }
    
    const date = new Date(input);
    return isNaN(date.getTime()) ? undefined : date;
  };

  const parseUserInputDateRange = (input: string): DateRange | undefined => {
    if (!input || input.trim() === "") {
      return undefined;
    }
    
    const parts = input.split(" - ").map(part => part.trim());
    if (parts.length === 2) {
      const from = parseUserInputDate(parts[0]);
      const to = parseUserInputDate(parts[1]);
      if (from && to) {
        return { from, to };
      }
    } else if (parts.length === 1) {
      const from = parseUserInputDate(parts[0]);
      if (from) {
        return { from, to: undefined };
      }
    }
    
    return undefined;
  };

  const handleOpenCalendar = () => {
    setFocus(true);
  };

  const normalizeDate = useCallback((date: Date): Date => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }, []);

  const isDateInRange = useCallback((date: Date): boolean => {
    const normalizedDate = normalizeDate(date);
    if (normalizedMinDate) {
      if (normalizedDate < normalizedMinDate) {
        return false;
      }
    }
    if (normalizedMaxDate) {
      if (normalizedDate > normalizedMaxDate) {
        return false;
      }
    }
    return true;
  }, [normalizedMinDate, normalizedMaxDate, normalizeDate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setIsTyping(true);
    setValue(inputValue);
    
    if (props.type === "single") {
      const parsedDate = parseUserInputDate(inputValue);
      if (parsedDate) {
        if (isDateInRange(parsedDate)) {
          setSelected(parsedDate);
          setSelectedDate(parsedDate);
        }
      } else if (inputValue === "") {
        setSelected(undefined);
      }
    } else if (props.type === "range") {
      const parsedRange = parseUserInputDateRange(inputValue);
      if (parsedRange) {
        const validFrom = !parsedRange.from || isDateInRange(parsedRange.from);
        const validTo = !parsedRange.to || isDateInRange(parsedRange.to);
        if (validFrom && validTo) {
          setSelectedDateRange(parsedRange);
        }
      } else if (inputValue === "") {
        setSelectedDateRange({ from: undefined, to: undefined });
      }
    }
  };

  const handleInputBlur = useCallback(() => {
    setIsTyping(false);
    
    if (props.type === "single") {
      const parsedDate = parseUserInputDate(value);
      if (parsedDate) {
        if (isDateInRange(parsedDate)) {
          const formattedDate = formatDateToString(parsedDate);
          setAppliedDate(parsedDate);
          setSelected(parsedDate);
          setSelectedDate(parsedDate);
          setValue(formattedDate);
          props.onChange(formattedDate);
        } else {
          if (appliedDate) {
            const formattedDate = formatDateToString(appliedDate);
            setValue(formattedDate);
            setSelected(appliedDate);
            setSelectedDate(appliedDate);
          } else {
            setValue("");
            setSelected(undefined);
            props.onChange("");
          }
        }
      } else if (value === "") {
        setSelected(undefined);
        setAppliedDate(undefined);
        setValue("");
        props.onChange("");
      }
    } else if (props.type === "range") {
      const parsedRange = parseUserInputDateRange(value);
      if (parsedRange) {
        const validFrom = !parsedRange.from || isDateInRange(parsedRange.from);
        const validTo = !parsedRange.to || isDateInRange(parsedRange.to);
        
        if (validFrom && validTo) {
          let formattedRange = "";
          if (parsedRange.from && parsedRange.to) {
            formattedRange = `${formatDateToString(parsedRange.from)} - ${formatDateToString(parsedRange.to)}`;
          } else if (parsedRange.from) {
            formattedRange = formatDateToString(parsedRange.from);
          }
          setAppliedDateRange(parsedRange);
          setSelectedDateRange(parsedRange);
          setValue(formattedRange);
          props.onChange(formattedRange);
        } else {
          if (appliedDateRange?.from || appliedDateRange?.to) {
            let formattedRange = "";
            if (appliedDateRange.from && appliedDateRange.to) {
              formattedRange = `${formatDateToString(appliedDateRange.from)} - ${formatDateToString(appliedDateRange.to)}`;
            } else if (appliedDateRange.from) {
              formattedRange = formatDateToString(appliedDateRange.from);
            }
            setValue(formattedRange);
            setSelectedDateRange(appliedDateRange);
          } else {
            setValue("");
            setSelectedDateRange({ from: undefined, to: undefined });
            props.onChange("");
          }
        }
      } else if (value === "") {
        setSelectedDateRange({ from: undefined, to: undefined });
        setAppliedDateRange({ from: undefined, to: undefined });
        setValue("");
        props.onChange("");
      }
    }
  }, [props.type, value, props.onChange, appliedDate, appliedDateRange, isDateInRange]);

  useEffect(() => {
    if (prevPropsValueRef.current === props.value) {
      return;
    }
    
    prevPropsValueRef.current = props.value;
    
    if (props.type === "single" && props.value) {
      setValue(props.value);
      
      const defaultDate = parseUserInputDate(props.value) || parseDateString(props.value);
      if (defaultDate && !isNaN(defaultDate.getTime())) {
        if (isDateInRange(defaultDate)) {
          setSelected(defaultDate);
          setSelectedDate(defaultDate);
          setAppliedDate(defaultDate);
        } else {
          setSelectedDate(defaultDate);
          setAppliedDate(defaultDate);
        }
      }
    } else if (props.type === "range" && props.value) {
      setValue(props.value);
      
      const [fromString, toString] = props.value.split(" - ");

      const fromDate = parseUserInputDate(fromString) || parseDateString(fromString);
      const toDate = parseUserInputDate(toString) || parseDateString(toString);
      if (fromDate && toDate) {
        setSelectedDateRange({ from: fromDate, to: toDate });
        setAppliedDateRange({ from: fromDate, to: toDate });
      }
    } else if (!props.value) {
      setValue("");
      setSelected(undefined);
      setAppliedDate(undefined);
    }
  }, [props.value, props.type, isDateInRange]);

  useEffect(() => {
    if (!focus && (!value || value === "")) {
      setSelected(undefined);
      setSelectedDate(new Date());
      setSelectedDateRange({ from: undefined, to: undefined });
    }
  }, [focus, appliedDate, appliedDateRange, value]);

  let footer = (
    <div className="flex flex-row formsection items-center p-2">
      <NewButton
        onClick={() => setFocus(false)}
        className="flex-1 !pt-[16px]"
        icon={<Close />}
        color="neutral"
        content="iconSquare"
        size="large"
        style="ghost"
      />
      <NewButton
        text="Today"
        className={`!text-lg ${isStaffMode ? "secondary" : "primary"}`}
        onClick={() => {
          const today = new Date();
          if (!props.minDate || !props.maxDate || isDateInRange(today)) {
            setSelected(today);
            setSelectedDate(today);
            setSelectedDateRange({
              from: today,
              to: undefined,
            });
          }
        }}
        size="medium"
        style="ghost"
        color="primary"
      />
      {props.onClear && (
        <button
          onClick={() => {
            setSelected(undefined);
            setSelectedDate(new Date());
            setSelectedDateRange(undefined);
            setAppliedDate(undefined);
            setAppliedDateRange(undefined);
            setValue("");
            setIsTyping(false);
            setFocus(false);
            props.onClear?.();
          }}
          className="text-gray-font w-6 h-6 flex items-center justify-center hover:text-primary transition-colors"
          type="button"
        >
          <TrashCan />
        </button>
      )}
      <NewButton
        text="apply"
        className="!text-lg calendar-apply-button"
        onClick={() => {
          setIsTyping(false);
          setAppliedDate(selected);
          setAppliedDateRange(selectedDateRange);
          setFocus(false);
        }}
        size="medium"
        style="ghost"
        color="primary"
      />
    </div>
  );

  useEffect(() => {
    if (!isTyping) {
      let stringAux: string = "";
      if (props.type === "single") {
        if (appliedDate) {
          stringAux = stringAux.concat(`${formatDateToString(appliedDate)}`);
          props.onChange(stringAux);
          setValue(stringAux);
        }
      } else if (props.type === "range") {
        if (!!appliedDateRange?.from && !!appliedDateRange?.to) {
          stringAux = `${formatDateToString(appliedDateRange.from)} - ${formatDateToString(appliedDateRange.to)}`;
          props.onChange(stringAux);
          setValue(stringAux);
        } else if (!!appliedDateRange?.from) {
          stringAux = `${formatDateToString(appliedDateRange.from)}`;
          props.onChange(stringAux);
          setValue(stringAux);
        } else if (!!appliedDateRange?.to) {
          stringAux = `${formatDateToString(appliedDateRange.to)}`;
          props.onChange(stringAux);
          setValue(stringAux);
        }
      }
    }
  }, [appliedDateRange, appliedDate, isTyping]);

  useEffect(() => {
    if (props.value === "") {
      setValue("");
    }
  }, [props.value]);

  useEffect(() => {
    if (props.defaultValue) {
      setAppliedDate(new Date(props.defaultValue));
    }
  }, [props.defaultValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        if (isTyping) {
          handleInputBlur();
        }
        setFocus(false);
      }
    };

    if (focus) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [focus, isTyping, handleInputBlur]);

  useEffect(() => {
    const calculateCalendarPosition = () => {
      if (!focus || !calendarRef.current || !calendarDropdownRef.current) {
        return;
      }

      const inputRect = calendarRef.current.getBoundingClientRect();
      const calendarHeight = calendarDropdownRef.current.offsetHeight || 400; // Altura estimada del calendario
      const spaceBelow = window.innerHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;
      const minSpaceRequired = calendarHeight + 20;

      if (spaceBelow < minSpaceRequired && spaceAbove > minSpaceRequired) {
        setShowCalendarAbove(true);
      } else {
        setShowCalendarAbove(false);
      }
    };

    if (!focus) {
      if (cleanupRef.current.timeoutId1) {
        clearTimeout(cleanupRef.current.timeoutId1);
      }
      if (cleanupRef.current.timeoutId2) {
        clearTimeout(cleanupRef.current.timeoutId2);
      }
      if (cleanupRef.current.checkInterval) {
        clearInterval(cleanupRef.current.checkInterval);
      }
      if (cleanupRef.current.resizeObserver) {
        cleanupRef.current.resizeObserver.disconnect();
      }
      cleanupRef.current = {};
      return;
    }

    cleanupRef.current.timeoutId1 = setTimeout(() => {
      calculateCalendarPosition();
      cleanupRef.current.timeoutId2 = setTimeout(calculateCalendarPosition, 100);
      
      window.addEventListener('resize', calculateCalendarPosition);
      window.addEventListener('scroll', calculateCalendarPosition, true);

      cleanupRef.current.checkInterval = setInterval(() => {
        if (calendarDropdownRef.current && !cleanupRef.current.resizeObserver) {
          cleanupRef.current.resizeObserver = new ResizeObserver(() => {
            calculateCalendarPosition();
          });
          cleanupRef.current.resizeObserver.observe(calendarDropdownRef.current);
          if (cleanupRef.current.checkInterval) {
            clearInterval(cleanupRef.current.checkInterval);
            cleanupRef.current.checkInterval = undefined;
          }
        }
      }, 50);
    }, 0);

    return () => {
      if (cleanupRef.current.timeoutId1) {
        clearTimeout(cleanupRef.current.timeoutId1);
      }
      if (cleanupRef.current.timeoutId2) {
        clearTimeout(cleanupRef.current.timeoutId2);
      }
      if (cleanupRef.current.checkInterval) {
        clearInterval(cleanupRef.current.checkInterval);
      }
      window.removeEventListener('resize', calculateCalendarPosition);
      window.removeEventListener('scroll', calculateCalendarPosition, true);
      if (cleanupRef.current.resizeObserver) {
        cleanupRef.current.resizeObserver.disconnect();
      }
      cleanupRef.current = {};
    };
  }, [focus]);

  return (
    <div
      ref={calendarRef}
      className={`flex flex-col items-start w-full relative `}
    >
      <div className="flex flex-col gap-2 md:gap-[.625rem] w-full">
        {props.title && (
          <div className="flex gap-2 w-full">
            <InputLabel
              name="calendar-input"
              title={props.title}
              required={props.required}
              tooltip={props.tooltip}
            />
          </div>
        )}
        <div className="relative w-full">
          <input
            type="text"
            value={value || ""}
            placeholder={"MM/DD/YYYY"}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            disabled={props.disabled}
            className={`w-full font-sans text-base md:text-lg not-italic font-normal leading-5 flex py-[6px] md:py-3 px-2 md:px-4 items-center gap-2 border-r-0 border-gray-25 bg-gray-25 border border-solid outline-none disabled:outline disabled:border-none disabled:outline-offset-0 disabled:outline-gray-300 disabled:text-gray-400 ${props.error ? "errorInput text-primary" : "text-input-gray"} pr-10`}
          />
          <button
            type="button"
            onClick={handleOpenCalendar}
            disabled={props.disabled}
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${props.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} focus:outline-none`}
            aria-label="Abrir calendario"
          >
            <span className={`${props.disabled ? 'text-gray-200' : 'text-input-gray'} w-5 h-5 md:w-6 md:h-6 flex items-center justify-center`}>
              <CalendarMonth />
            </span>
          </button>
        </div>
        <span
          className={`flex flex-wrap items-center ${!props.error && "hidden"} text-base font-medium text-primary`}
        >
          {props.errorMsj}
        </span>
      </div>
      {focus && props.type === "single" && (
        <div 
          ref={calendarDropdownRef}
          className={`absolute right-0 ${showCalendarAbove ? 'bottom-full mb-2' : 'top-20'} bg-neutral z-50 ${colorClass}`}
        >
          <DayPicker
            mode="single"
            footer={footer}
            selected={selected}
            onSelect={(date) => {
              if (date && isDateInRange(date)) {
                setSelected(date);
                setSelectedDate(date);
              }
            }}
            month={selectedDate}
            onMonthChange={setSelectedDate}
            fixedWeeks
            captionLayout="dropdown-buttons"
            fromYear={1999}
            toYear={2100}
            fromDate={normalizedMinDate}
            toDate={normalizedMaxDate}
            disabled={(date) => {
              const normalizedDate = new Date(date);
              normalizedDate.setHours(0, 0, 0, 0);
              
              if (normalizedMinDate) {
                if (normalizedDate < normalizedMinDate) {
                  return true;
                }
              }
              if (normalizedMaxDate) {
                if (normalizedDate > normalizedMaxDate) {
                  return true;
                }
              }
              return false;
            }}
          />
        </div>
      )}
      {focus && props.type === "range" && (
        <div 
          ref={calendarDropdownRef}
          className={`absolute ${showCalendarAbove ? 'bottom-full mb-2' : 'top-20'} right-0 bg-neutral z-50 ${colorClass}`}
        >
          <DayPicker
            mode="range"
            footer={footer}
            selected={selectedDateRange}
            onSelect={(range) => {
              if (range) {
                const validFrom = !range.from || isDateInRange(range.from);
                const validTo = !range.to || isDateInRange(range.to);
                if (validFrom && validTo) {
                  setSelectedDateRange(range);
                } else if (validFrom && !range.to) {
                  setSelectedDateRange({ from: range.from, to: undefined });
                }
              } else {
                setSelectedDateRange(undefined);
              }
            }}
            month={selected}
            onMonthChange={setSelected}
            captionLayout="dropdown-buttons"
            fromYear={1999}
            toYear={2100}
            fixedWeeks
            fromDate={normalizedMinDate}
            toDate={normalizedMaxDate}
            disabled={(date) => {
              const normalizedDate = new Date(date);
              normalizedDate.setHours(0, 0, 0, 0);
              
              if (normalizedMinDate) {
                if (normalizedDate < normalizedMinDate) {
                  return true;
                }
              }
              if (normalizedMaxDate) {
                if (normalizedDate > normalizedMaxDate) {
                  return true;
                }
              }
              return false;
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CalendarInput;
